// src/routes/merchants/picker/merchantPickerModel.ts
import type { MerchantIdentity } from '../../../api/merchant'
import type { components } from '../../../api/generated/schema'

/** Eight logo tiles: two rows of four on tablet and up, three rows (less one) of three on a phone. */
export const MOST_USED_MAX = 8

/** One choice in the picker, wherever it came from: a merchants page, a search, or a transaction's embedded brief. */
export type PickerMerchant = { id: string; name: string; identity: MerchantIdentity }

type Merchant = components['schemas']['MerchantResponse']

export const toPickerMerchant = (m: Merchant): PickerMerchant => ({ id: m.id, name: m.name, identity: m })

const fold = (s: string) => s.trim().toLocaleLowerCase()

/** 0 the name IS q · 1 starts with q · 2 a word in it does · 3 contains q. `searchPicker`'s order, with exact first so Enter never picks "Costco Gas" over "Costco". */
function rank(name: string, q: string): number | null {
  const n = fold(name)
  if (n === q) return 0
  if (n.startsWith(q)) return 1
  if (n.split(/[^\p{L}\p{N}]+/u).some((w) => w.startsWith(q))) return 2
  if (n.includes(q)) return 3
  return null
}

/** Every merchant in `pool` whose name matches, best first. Blank → none. */
export function searchMerchants(pool: readonly PickerMerchant[], query: string): PickerMerchant[] {
  const q = fold(query)
  if (q === '') return []
  const scored: { m: PickerMerchant; r: number }[] = []
  for (const m of pool) {
    const r = rank(m.name, q)
    if (r !== null) scored.push({ m, r })
  }
  return scored.sort((a, b) => a.r - b.r || a.m.name.localeCompare(b.m.name)).map((s) => s.m)
}

/** One list from several, deduped by id; the first occurrence wins and order is kept. */
export function mergeMerchants(...lists: readonly (readonly PickerMerchant[])[]): PickerMerchant[] {
  const byId = new Map<string, PickerMerchant>()
  for (const list of lists) for (const m of list) if (!byId.has(m.id)) byId.set(m.id, m)
  return [...byId.values()]
}

/** The merchant whose name equals `query`, case-insensitively — the one "Create" must never duplicate. */
export function exactMatch(pool: readonly PickerMerchant[], query: string): PickerMerchant | undefined {
  const q = fold(query)
  return q === '' ? undefined : pool.find((m) => fold(m.name) === q)
}
